import { PerspectiveCamera, Vector3 } from 'three';

/**
 *  @fileOverview A simple orbit camera. Camera position is updated only when 'update' is called. Theta is longitude, Phi is latitude
 *  @author Barnaby Sheeran, George Fuller
 */

export default class CameraOrbitPositionable {
	constructor(options = {}) {
		// Perspective Camera Options
		const ASPECT_RATIO = options.cameraAspectRatio
			? options.cameraAspectRatio
			: 1.0;

		const NEAR = options.cameraNear ? options.cameraNear : 1.0;
		const FAR = options.cameraFar ? options.cameraFar : 100.0;

		// FOV
		this.fov = options.cameraFov ? options.cameraFov : 45.0;

		// Create Perspective Camera
		this.CAMERA = new PerspectiveCamera(this.fov, ASPECT_RATIO, NEAR, FAR);

		// Position
		this.POSITION_LOOKAT = options.positionLookat
			? options.positionLookat
			: new Vector3(0.0, 0.0, 0.0);

		// Radius / Zoom
		this.RADIUS_MIN = options.radiusMin ? options.radiusMin : 5.0;
		this.RADIUS_MAX = options.radiusMax ? options.radiusMax : 50.0;

		this.RADIUS_DELTA = options.radiusDelta ? options.radiusDelta : 5.0;
		this.RADIUS_LERP = options.radiusLerp ? options.radiusLerp : 0.1;

		this.radius = options.radiusInitial ? options.radiusInitial : 35.0;
		this.radiusTarget = this.radius;

		// Rotation
		const ROTATION_SPEED = options.rotationSpeed
			? options.rotationSpeed
			: 600.0; // This is pixel distance dragged to do a 1 Pi / 180 degree turn.

		this.ROTATION_RAD_PER_PIXEL = Math.PI / ROTATION_SPEED;

		this.ROTATION_LERP = options.rotationLerp ? options.rotationLerp : 0.1;

		this.ROTATION_PHI_MIN = options.rotationPhiMin
			? options.rotationPhiMin
			: Math.PI * 0.5;

		this.ROTATION_PHI_MAX = options.rotationPhiMax
			? options.rotationPhiMax
			: Math.PI * 0.2;

		this.rotation_theta = options.rotationThetaInitial
			? options.rotationThetaInitial
			: 0.0;

		this.rotation_phi = options.rotationPhiInitial
			? options.rotationPhiInitial
			: Math.PI * 0.5;

		this.rotation_target_theta = this.rotation_theta;
		this.rotation_target_phi = this.rotation_phi;

		// Init Position
		const SIN_THETA = Math.sin(this.rotation_theta);
		const COS_THETA = Math.cos(this.rotation_theta);

		const SIN_PHI = Math.sin(this.rotation_phi);
		const COS_PHI = Math.cos(this.rotation_phi);

		this.CAMERA.position.x =
			this.POSITION_LOOKAT.x + SIN_PHI * SIN_THETA * this.radius;

		this.CAMERA.position.y = this.POSITION_LOOKAT.y + COS_PHI * this.radius;

		this.CAMERA.position.z =
			this.POSITION_LOOKAT.z + SIN_PHI * COS_THETA * this.radius;

		this.CAMERA.lookAt(this.POSITION_LOOKAT);

		// Orbit
		this.isOrbit = true;
	}

	// ____________________________________________________________________ Update

	update() {
		const { ROTATION_LERP } = this;
		const { CAMERA } = this;
		const { POSITION_LOOKAT } = this;

		if (this.isOrbit === true) {
			// Zoom to Target
			this.radius += (this.radiusTarget - this.radius) * this.RADIUS_LERP;

			// Rotate to Target
			this.rotation_theta +=
				(this.rotation_target_theta - this.rotation_theta) * ROTATION_LERP;

			this.rotation_phi +=
				(this.rotation_target_phi - this.rotation_phi) * ROTATION_LERP;

			// Position
			const SIN_THETA = Math.sin(this.rotation_theta);
			const COS_THETA = Math.cos(this.rotation_theta);

			const SIN_PHI = Math.sin(this.rotation_phi);
			const COS_PHI = Math.cos(this.rotation_phi);

			CAMERA.position.x = POSITION_LOOKAT.x + SIN_PHI * SIN_THETA * this.radius;
			CAMERA.position.y = POSITION_LOOKAT.y + COS_PHI * this.radius;
			CAMERA.position.z = POSITION_LOOKAT.z + SIN_PHI * COS_THETA * this.radius;

			// Look At
			CAMERA.lookAt(this.POSITION_LOOKAT);
		}
	}

	// _____________________________________________________________________ Orbit

	startOrbit() {
		const { CAMERA } = this;

		// Reset FOV
		CAMERA.fov = this.fov;
		CAMERA.updateProjectionMatrix();

		// Orbiting
		this.isOrbit = true;
	}

	stopOrbit() {
		this.isOrbit = false;
	}

	// __________________________________________________________________ Position

	setPosition(x, y, z) {
		const { CAMERA } = this;

		CAMERA.position.x = x;
		CAMERA.position.y = y;
		CAMERA.position.z = z;
	}

	setPositionLookAt(x, y, z) {
		this.CAMERA.lookAt(new Vector3(x, y, z));
	}

	setFOV(fov) {
		const { CAMERA } = this;

		CAMERA.fov = fov;
		CAMERA.updateProjectionMatrix();
	}

	// ____________________________________________________________________ Rotate

	rotateHorizontal(pixelDeltaX) {
		this.rotation_target_theta -= pixelDeltaX * this.ROTATION_RAD_PER_PIXEL;
	}

	rotateVertical(pixelDeltaY) {
		const { ROTATION_PHI_MIN } = this;
		const { ROTATION_PHI_MAX } = this;

		this.rotation_target_phi -= pixelDeltaY * this.ROTATION_RAD_PER_PIXEL;

		// Bound
		if (this.rotation_target_phi > ROTATION_PHI_MIN) {
			this.rotation_target_phi = ROTATION_PHI_MIN;
		}

		if (this.rotation_target_phi < ROTATION_PHI_MAX) {
			this.rotation_target_phi = ROTATION_PHI_MAX;
		}
	}

	// ______________________________________________________________________ Zoom

	zoom(proportion = 1.0) {
		const { RADIUS_MIN } = this;
		const { RADIUS_MAX } = this;

		this.radiusTarget += this.RADIUS_DELTA * proportion * 0.1;

		// Bound
		if (this.radiusTarget < RADIUS_MIN) {
			this.radiusTarget = RADIUS_MIN;
		}

		if (this.radiusTarget > RADIUS_MAX) {
			this.radiusTarget = RADIUS_MAX;
		}
	}

	// _____________________________________________________________________ Shape

	setSize(width, height) {
		this.CAMERA.aspect = width / height;
		this.CAMERA.updateProjectionMatrix();
	}

	// ____________________________________________________________________ Access

	getCamera() {
		return this.CAMERA;
	}

	getCameraPosition() {
		return this.CAMERA.position;
	}
}
